import { useMemo, useState } from 'react'
import { Plus, X, Loader2, ChevronDown, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { usePageTranslation } from '@/i18n/useTranslation'
import { lineCampaignTranslations as enT } from '@/i18n/locales/en/lineCampaign'
import { lineCampaignTranslations as thT } from '@/i18n/locales/th/lineCampaign'
import type {
  LineCampaignFilterCondition,
  LineCampaignFilterType,
  LineCampaignFilterOptions,
} from '@/lib/api/controlPlaneClient'

// What each condition type needs from the UI: a scope picker (collection/product),
// and/or a numeric "extra" value. Labels/groups/suffixes are i18n keys resolved at
// render — this map holds only structure, no display strings.
type Scope = 'collection' | 'product' | 'none'
type Extra = null | 'amount' | 'qty' | 'days'
type GroupKey = 'purchased' | 'didnt_buy' | 'spend' | 'quantity' | 'recency'
type SuffixKey = 'baht' | 'units' | 'orders' | 'days' | null

const TYPE_META: Record<
  LineCampaignFilterType,
  { scope: Scope; extra: Extra; suffix: SuffixKey; group: GroupKey }
> = {
  bought_collection: { scope: 'collection', extra: null, suffix: null, group: 'purchased' },
  bought_product: { scope: 'product', extra: null, suffix: null, group: 'purchased' },
  not_bought_collection: { scope: 'collection', extra: null, suffix: null, group: 'didnt_buy' },
  not_bought_product: { scope: 'product', extra: null, suffix: null, group: 'didnt_buy' },
  spend_on_collection: { scope: 'collection', extra: 'amount', suffix: 'baht', group: 'spend' },
  spend_on_product: { scope: 'product', extra: 'amount', suffix: 'baht', group: 'spend' },
  total_spend: { scope: 'none', extra: 'amount', suffix: 'baht', group: 'spend' },
  qty_of_collection: { scope: 'collection', extra: 'qty', suffix: 'units', group: 'quantity' },
  qty_of_product: { scope: 'product', extra: 'qty', suffix: 'units', group: 'quantity' },
  order_count: { scope: 'none', extra: 'qty', suffix: 'orders', group: 'quantity' },
  purchased_within: { scope: 'none', extra: 'days', suffix: 'days', group: 'recency' },
  not_purchased_within: { scope: 'none', extra: 'days', suffix: 'days', group: 'recency' },
}

const GROUP_KEYS: GroupKey[] = ['purchased', 'didnt_buy', 'spend', 'quantity', 'recency']

function newCondition(): LineCampaignFilterCondition {
  return { connector: 'and', type: 'bought_collection', item_groups: [], item_codes: [], amount: 0, qty: 0, days: 0 }
}

// --- Searchable multi-select (collections / products) ---
function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  loading,
}: {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  placeholder: string
  loading?: boolean
}) {
  const t = usePageTranslation({ en: enT, th: thT })
  const [open, setOpen] = useState(false)
  const selectedSet = useMemo(() => new Set(selected), [selected])
  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? t.nSelected.replace('{n}', '1')
        : t.nSelected.replace('{n}', String(selected.length))

  const toggle = (value: string) => {
    const next = new Set(selectedSet)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(Array.from(next))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full sm:flex-1 sm:min-w-[280px] justify-between gap-2 rounded-lg font-normal text-[15px] bg-white"
        >
          <span className={`truncate text-left ${selected.length === 0 ? 'text-muted-foreground' : ''}`}>{label}</span>
          {loading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(460px,90vw)]" align="start">
        <Command>
          <CommandInput placeholder={t.search} className="h-11 text-[15px]" />
          <CommandList className="max-h-[340px]">
            <CommandEmpty>{t.noMatches}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => toggle(o.value)} className="items-start gap-2.5 py-2.5 text-[15px]">
                  <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border shrink-0 ${selectedSet.has(o.value) ? 'bg-[#06C755] border-[#06C755] text-white' : 'border-gray-300'}`}>
                    {selectedSet.has(o.value) && <Check className="w-3.5 h-3.5" />}
                  </span>
                  <span className="flex-1 whitespace-normal leading-snug break-words">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function AudienceFilterBuilder({
  conditions,
  onChange,
  options,
  optionsLoading,
}: {
  conditions: LineCampaignFilterCondition[]
  onChange: (next: LineCampaignFilterCondition[]) => void
  options: LineCampaignFilterOptions | undefined
  optionsLoading: boolean
}) {
  const t = usePageTranslation({ en: enT, th: thT })
  const collectionOpts = useMemo(
    () => (options?.collections ?? []).map((c) => ({ value: c, label: c })),
    [options]
  )
  const productOpts = useMemo(
    () => (options?.products ?? []).map((p) => ({ value: p.item_code, label: p.item_name })),
    [options]
  )

  const patch = (i: number, p: Partial<LineCampaignFilterCondition>) =>
    onChange(conditions.map((c, idx) => (idx === i ? { ...c, ...p } : c)))
  const remove = (i: number) => onChange(conditions.filter((_, idx) => idx !== i))
  const add = () => onChange([...conditions, newCondition()])

  return (
    <div className="space-y-3">
      {conditions.map((cond, i) => {
        const meta = TYPE_META[cond.type]
        return (
          <div key={i} className="flex flex-wrap items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
            {/* Lead-in reads as a sentence: "Customers who …" then and/or joins */}
            {i === 0 ? (
              <span className="text-[15px] font-medium text-gray-700 shrink-0">{t.customersWho}</span>
            ) : (
              <Select value={cond.connector} onValueChange={(v) => patch(i, { connector: v as 'and' | 'or' })}>
                <SelectTrigger className="h-11 w-[76px] rounded-lg shrink-0 text-[15px] font-medium bg-white border border-input px-3 [&>span]:text-left"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="and">{t.connAnd}</SelectItem>
                  <SelectItem value="or">{t.connOr}</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Condition type */}
            <Select value={cond.type} onValueChange={(v) => patch(i, { type: v as LineCampaignFilterType })}>
              <SelectTrigger className="h-11 rounded-lg min-w-[230px] text-[15px] bg-white border border-input px-3 [&>span]:text-left"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GROUP_KEYS.map((g) => (
                  <SelectGroup key={g}>
                    <SelectLabel>{t[`group_${g}`]}</SelectLabel>
                    {(Object.keys(TYPE_META) as LineCampaignFilterType[])
                      .filter((tp) => TYPE_META[tp].group === g)
                      .map((tp) => (
                        <SelectItem key={tp} value={tp}>{t[`type_${tp}`]}</SelectItem>
                      ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>

            {/* Scope picker */}
            {meta.scope === 'collection' && (
              <MultiSelect
                options={collectionOpts}
                selected={cond.item_groups ?? []}
                onChange={(v) => patch(i, { item_groups: v })}
                placeholder={t.selectCollections}
                loading={optionsLoading}
              />
            )}
            {meta.scope === 'product' && (
              <MultiSelect
                options={productOpts}
                selected={cond.item_codes ?? []}
                onChange={(v) => patch(i, { item_codes: v })}
                placeholder={t.selectProducts}
                loading={optionsLoading}
              />
            )}

            {/* Numeric extra */}
            {meta.extra && (
              <div className="flex items-center gap-2">
                <Input
                  inputMode="numeric"
                  value={String(
                    meta.extra === 'amount' ? cond.amount ?? '' : meta.extra === 'qty' ? cond.qty ?? '' : cond.days ?? ''
                  ).replace(/^0$/, '')}
                  placeholder="0"
                  onChange={(e) => {
                    const n = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0
                    patch(i, meta.extra === 'amount' ? { amount: n } : meta.extra === 'qty' ? { qty: n } : { days: n })
                  }}
                  className="h-11 w-28 rounded-lg text-[15px]"
                />
                {meta.suffix && <span className="text-sm text-muted-foreground">{t[`suffix_${meta.suffix}`]}</span>}
              </div>
            )}

            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 shrink-0"
              aria-label={t.removeCondition}
            >
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        )
      })}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" onClick={add} className="h-10 rounded-lg gap-1.5 text-[15px]">
          <Plus className="w-4 h-4" /> {t.addCondition}
        </Button>
        {conditions.length > 1 && (
          <span className="text-xs text-muted-foreground">{t.conditionsHelp}</span>
        )}
      </div>
    </div>
  )
}
